import { ProviderProgramTaxonomy } from '../../../models/providers/Providers'
import { ProgramCategory, SessionCountByProgram, SessionLimitByProgram } from '../../../models/session/SessionUsage'
import { FOURTEEN_DAYS_TO_MS, SIXTY_DAYS_TO_MS, THIRTY_DAYS_TO_MS } from '../../calendar/types'
import { isBCMProvider, isCLEProvider, isNeurodiversityProvider } from '../../providers/utils'
import { TREATMENT_OPTIONS_FROM_TRIAGE_BACKEND, TREATMENT_OPTIONS_TYPE } from '../constants/treatmentOptions'

export enum SESSIONS_COVERAGE_TITLE_ID {
  SESSIONS_TITLE = 'sessionsTitle',
  TOTAL_SESSIONS_TITLE = 'totalSessionsTitle',
  THERAPY_SESSIONS_TITLE = 'therapySessionsTitle',
  COACHING_SESSIONS_TITLE = 'coachingSessionsTitle',
  UNLIMITED_SESSIONS_TITLE = 'unlimitedSessionsTitle',
}

export const getProviderAvailabilityFetchDuration = (programTaxonomy?: ProviderProgramTaxonomy[]): number => {
  const taxonomy = programTaxonomy ?? []

  // BCM and CLE providers get 60 days
  if (isBCMProvider({ programTaxonomy: taxonomy }) || isCLEProvider({ programTaxonomy: taxonomy })) {
    return SIXTY_DAYS_TO_MS
  }

  // Neurodiversity providers get 30 days
  if (isNeurodiversityProvider({ programTaxonomy: taxonomy })) {
    return THIRTY_DAYS_TO_MS
  }

  // All other providers get 14 days
  return FOURTEEN_DAYS_TO_MS
}

export function getSessionLimitProgramCategoryByTreatmentOption(
  treatmentOption: TREATMENT_OPTIONS_TYPE,
): ProgramCategory {
  switch (treatmentOption) {
    case TREATMENT_OPTIONS_FROM_TRIAGE_BACKEND.COACHING:
    case TREATMENT_OPTIONS_FROM_TRIAGE_BACKEND.LIVE_MESSAGING_COACHING:
    case TREATMENT_OPTIONS_FROM_TRIAGE_BACKEND.GUIDED_SELF_CARE:
      return 'coaching'
    default:
      // all other treatment options are considered therapy
      return 'therapy'
  }
}

export const createRemainingSessionCountByProgram = (
  EAPSessionLimitByProgram?: SessionLimitByProgram,
  usedEAPSessionCountByProgram?: SessionCountByProgram,
): SessionCountByProgram => {
  if (!EAPSessionLimitByProgram) {
    return {}
  }
  return Object.keys(EAPSessionLimitByProgram).reduce(
    (sessionsRemainingByProgram: SessionCountByProgram, program: string) => {
      const programCategory = program as ProgramCategory
      const remainingSessionCount =
        EAPSessionLimitByProgram[programCategory] - (usedEAPSessionCountByProgram?.[programCategory] ?? 0)
      sessionsRemainingByProgram[programCategory] = Math.max(remainingSessionCount, 0)
      return sessionsRemainingByProgram
    },
    {} as SessionCountByProgram,
  )
}

/** `displaySessions` to be used with the `messageId` to display the correct sessions count message */
export type SessionCountForDisplay = {
  therapySessions: string | number | undefined
  coachingSessions: string | number | undefined
  displaySessions: string | number | undefined
  messageId: SESSIONS_COVERAGE_TITLE_ID
}

export const getSessionCountForDisplay = ({
  numSessions,
  numberOfSessionsByProgram,
  displayVisitsPerIssuePerYear,
  displayVisitsPerIssuePerYearByProgram,
}: {
  numSessions?: string
  numberOfSessionsByProgram?: SessionLimitByProgram
  displayVisitsPerIssuePerYear?: number
  displayVisitsPerIssuePerYearByProgram?: SessionCountByProgram
}): SessionCountForDisplay => {
  const result: SessionCountForDisplay = {
    therapySessions: numSessions,
    coachingSessions: numSessions,
    displaySessions: numSessions,
    messageId: SESSIONS_COVERAGE_TITLE_ID.SESSIONS_TITLE,
  }
  if (numSessions !== undefined && isUnlimitedSessionLimit(Number(numSessions))) {
    result.messageId = SESSIONS_COVERAGE_TITLE_ID.UNLIMITED_SESSIONS_TITLE
    result.displaySessions = undefined
  }

  if (displayVisitsPerIssuePerYearByProgram || numberOfSessionsByProgram) {
    let therapy = numberOfSessionsByProgram?.therapy
    let coaching = numberOfSessionsByProgram?.coaching
    if (displayVisitsPerIssuePerYearByProgram) {
      therapy = displayVisitsPerIssuePerYearByProgram.therapy
      coaching = displayVisitsPerIssuePerYearByProgram.coaching
    }

    result.coachingSessions = coaching
    result.therapySessions = therapy
    result.displaySessions = (therapy || 0) + (coaching || 0)
    result.messageId = displayVisitsPerIssuePerYearByProgram
      ? SESSIONS_COVERAGE_TITLE_ID.SESSIONS_TITLE
      : SESSIONS_COVERAGE_TITLE_ID.TOTAL_SESSIONS_TITLE

    if (
      isUnlimitedSessionLimit(Number(result.coachingSessions)) &&
      isUnlimitedSessionLimit(Number(result.therapySessions))
    ) {
      result.messageId = SESSIONS_COVERAGE_TITLE_ID.UNLIMITED_SESSIONS_TITLE
      result.displaySessions = undefined
    } else if (isUnlimitedSessionLimit(Number(result.coachingSessions))) {
      result.messageId = SESSIONS_COVERAGE_TITLE_ID.THERAPY_SESSIONS_TITLE
      result.displaySessions = result.therapySessions
    } else if (isUnlimitedSessionLimit(Number(result.therapySessions))) {
      result.messageId = SESSIONS_COVERAGE_TITLE_ID.COACHING_SESSIONS_TITLE
      result.displaySessions = result.coachingSessions
    }
  } else if (displayVisitsPerIssuePerYear) {
    result.coachingSessions = displayVisitsPerIssuePerYear
    result.therapySessions = displayVisitsPerIssuePerYear
    result.displaySessions = displayVisitsPerIssuePerYear
    result.messageId = SESSIONS_COVERAGE_TITLE_ID.SESSIONS_TITLE
    if (isUnlimitedSessionLimit(Number(result.displaySessions))) {
      result.messageId = SESSIONS_COVERAGE_TITLE_ID.UNLIMITED_SESSIONS_TITLE
      result.displaySessions = undefined
    }
  }

  return result
}

export const isProgramAvailableForMinor = (program: ProgramCategory): boolean => program === 'therapy'

export const UNLIMITED_SESSION_LIMIT = 999
export const isUnlimitedSessionLimit = (sessionLimit: number): boolean => sessionLimit === UNLIMITED_SESSION_LIMIT
